/**
 * Centralized app data store.
 *
 * Startup is instant: the song list is persisted to IndexedDB and read back on
 * launch so the UI renders immediately, then refreshed from the network in the
 * background (stale-while-revalidate). Per-song details (revisions + comments)
 * are loaded lazily the first time a song is opened, not up front.
 */

import Dexie, { Table } from 'dexie';
import { Song, SongRevision } from '@/types/song';
import { Comment } from '@/types/comment';
import { Album, AlbumSong } from '@/types/album';

interface CachedAlbumSongs {
  albumId: number;
  songs: AlbumSong[];
}

class AppDataDatabase extends Dexie {
  songs!: Table<Song, number>;
  albums!: Table<Album, number>;
  albumSongs!: Table<CachedAlbumSongs, number>;

  constructor() {
    super('songkit-appdata');
    this.version(1).stores({
      songs: 'songId',
    });
    // v2 adds an albums table for the album scope selector.
    this.version(2).stores({
      songs: 'songId',
      albums: 'albumId',
    });
    // v3 persists album track membership so fallback cover art can resolve from
    // cache instantly on a cold (slow Lambda) load instead of re-fetching it.
    this.version(3).stores({
      songs: 'songId',
      albums: 'albumId',
      albumSongs: 'albumId',
    });
  }
}

const db = new AppDataDatabase();

interface AppData {
  songs: Map<number, Song>;
  revisions: Map<number, SongRevision[]>;
  comments: Map<number, Comment[]>;
  albums: Map<number, Album>;
  albumSongs: Map<number, AlbumSong[]>;
  lastFetchTime: number | null;
}

class AppDataStore {
  private data: AppData = {
    songs: new Map(),
    revisions: new Map(),
    comments: new Map(),
    albums: new Map(),
    albumSongs: new Map(),
    lastFetchTime: null,
  };

  private initialized = false;
  private initPromise: Promise<void> | null = null;

  private detailsLoaded = new Set<number>();
  private detailsPromises = new Map<number, Promise<void>>();

  private albumDetailsLoaded = new Set<number>();
  private albumDetailsPromises = new Map<number, Promise<void>>();

  // Count of in-flight background refreshes (song list, album list, membership).
  // Drives the "Refreshing catalog" indicator.
  private refreshing = 0;

  private listeners = new Set<() => void>();

  /**
   * Subscribe to store changes (e.g. when a background refresh updates the
   * song list). Returns an unsubscribe function.
   */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify(): void {
    this.listeners.forEach((listener) => listener());
  }

  /** True while any background catalog refresh is in flight. */
  isRefreshing(): boolean {
    return this.refreshing > 0;
  }

  private beginRefresh(): void {
    this.refreshing += 1;
    this.notify();
  }

  private endRefresh(): void {
    this.refreshing = Math.max(0, this.refreshing - 1);
    this.notify();
  }

  /**
   * Ask the browser to make storage persistent so the WebView stops evicting
   * the cached catalog + cover art. Best-effort; safe to call repeatedly.
   */
  private async requestPersistentStorage(): Promise<void> {
    try {
      if (navigator.storage?.persist && navigator.storage.persisted) {
        const already = await navigator.storage.persisted();
        if (!already) {
          const granted = await navigator.storage.persist();
          console.log('[AppDataStore] Persistent storage', granted ? 'granted' : 'denied');
        }
      }
    } catch (error) {
      console.warn('[AppDataStore] persist() request failed:', error);
    }
  }

  /**
   * Initialize the store. Resolves as soon as there is data to render:
   * instantly from the IndexedDB cache when present, otherwise after the first
   * network fetch. A network refresh always runs (in the background when cache
   * exists).
   */
  async initialize(): Promise<void> {
    if (this.initialized) return;
    if (this.initPromise) return this.initPromise;

    // Resist WebView eviction of the cached catalog + cover art (best-effort).
    this.requestPersistentStorage();

    this.initPromise = this.loadInitial();
    await this.initPromise;
    this.initialized = true;
    this.initPromise = null;
  }

  private async loadInitial(): Promise<void> {
    let cached: Song[] = [];
    try {
      cached = await db.songs.toArray();
    } catch (error) {
      console.error('[AppDataStore] Error reading cached songs:', error);
    }

    // Seed albums from cache for an instant scope selector.
    try {
      const cachedAlbums = await db.albums.toArray();
      for (const album of cachedAlbums) {
        this.data.albums.set(album.albumId, album);
      }
    } catch (error) {
      console.error('[AppDataStore] Error reading cached albums:', error);
    }

    // Seed album track membership from cache so fallback cover art resolves
    // instantly — without waiting on the (cold) per-album detail requests.
    // Not marked as "loaded": a background refresh still runs once per session
    // to pick up new album assignments.
    try {
      const cachedAlbumSongs = await db.albumSongs.toArray();
      for (const row of cachedAlbumSongs) {
        this.data.albumSongs.set(row.albumId, row.songs);
      }
    } catch (error) {
      console.error('[AppDataStore] Error reading cached album membership:', error);
    }

    if (cached.length > 0) {
      for (const song of cached) {
        this.data.songs.set(song.songId, song);
      }
      // Render immediately from cache, refresh in the background.
      this.refreshSongList().catch((error) => {
        console.error('[AppDataStore] Background refresh failed:', error);
      });
    } else {
      // First launch (or empty cache): must fetch before we can render.
      await this.refreshSongList();
    }

    // Albums are always refreshed in the background; failure (e.g. the album
    // endpoints not yet deployed) is non-fatal — the selector simply hides.
    this.refreshAlbums().catch((error) => {
      console.warn('[AppDataStore] Album refresh failed:', error);
    });
  }

  /**
   * Fetch the song list (single request) and update both the in-memory map and
   * the IndexedDB cache. Notifies subscribers so the UI can re-render.
   */
  private async refreshSongList(): Promise<void> {
    this.beginRefresh();
    try {
      const apiClient = (await import('@/services/api/apiClient')).default;
      const response = await apiClient.get('/songs', { params: { limit: 1000 } });
      const songs: Song[] = response.data.songs;

      // Rebuild the map from the server response (handles deletions), merging in
      // any already-loaded detail fields (e.g. audioFileUrl) for songs we keep.
      const nextSongs = new Map<number, Song>();
      for (const song of songs) {
        const existing = this.data.songs.get(song.songId);
        nextSongs.set(song.songId, existing ? { ...existing, ...song } : song);
      }
      this.data.songs = nextSongs;
      this.data.lastFetchTime = Date.now();

      try {
        await db.transaction('rw', db.songs, async () => {
          await db.songs.clear();
          await db.songs.bulkPut(songs);
        });
      } catch (error) {
        console.error('[AppDataStore] Error persisting songs:', error);
      }

      this.notify();
    } finally {
      this.endRefresh();
    }
  }

  /**
   * Fetch the album list and update the in-memory map + IndexedDB cache.
   * Notifies subscribers so the scope selector reflects the latest albums.
   */
  private async refreshAlbums(): Promise<void> {
    this.beginRefresh();
    try {
      const apiClient = (await import('@/services/api/apiClient')).default;
      const response = await apiClient.get('/albums', { params: { limit: 1000 } });
      const albums: Album[] = response.data.albums || [];

      const nextAlbums = new Map<number, Album>();
      for (const album of albums) {
        nextAlbums.set(album.albumId, album);
      }
      this.data.albums = nextAlbums;

      try {
        await db.transaction('rw', db.albums, async () => {
          await db.albums.clear();
          await db.albums.bulkPut(albums);
        });
      } catch (error) {
        console.error('[AppDataStore] Error persisting albums:', error);
      }

      this.notify();
    } finally {
      this.endRefresh();
    }

    // Load track lists for albums that have artwork so songs missing their own
    // cover can fall back to an album cover (mirrors the desktop app).
    this.ensureAlbumArtworkMembership().catch((error) => {
      console.warn('[AppDataStore] Album membership load failed:', error);
    });
  }

  /**
   * Ensure track lists are loaded for every album that has cover art. Only
   * those can serve as a fallback cover, so art-less albums are skipped.
   */
  private async ensureAlbumArtworkMembership(): Promise<void> {
    this.beginRefresh();
    try {
      const albumsWithArt = Array.from(this.data.albums.values()).filter((a) => a.coverArtUrl);
      await Promise.all(
        albumsWithArt.map((a) => this.ensureAlbumDetails(a.albumId).catch(() => undefined))
      );
      // Track lists are in; re-render so fallback covers appear.
      this.notify();
    } finally {
      this.endRefresh();
    }
  }

  /**
   * The fallback album cover for a song without its own artwork: among the
   * albums (that have artwork) the song belongs to, the one with the lowest
   * AlbumId. This matches the desktop app, which orders a song's albums by
   * AlbumId ascending and takes the first with cover art. Selecting by min
   * AlbumId (rather than map iteration order) also keeps the choice stable
   * across the cached-seed vs. post-refresh states, so the art never flips.
   */
  getSongFallbackAlbum(songId: number): Album | undefined {
    let best: Album | undefined;
    for (const album of this.data.albums.values()) {
      if (!album.coverArtUrl) continue;
      const tracks = this.data.albumSongs.get(album.albumId);
      if (tracks && tracks.some((t) => t.songId === songId)) {
        if (!best || album.albumId < best.albumId) {
          best = album;
        }
      }
    }
    return best;
  }

  /**
   * Ensure an album's track list is loaded. Fetches once per session and caches
   * the result; concurrent callers share the same request.
   */
  async ensureAlbumDetails(albumId: number): Promise<void> {
    if (this.albumDetailsLoaded.has(albumId)) return;

    const inflight = this.albumDetailsPromises.get(albumId);
    if (inflight) return inflight;

    const promise = (async () => {
      const apiClient = (await import('@/services/api/apiClient')).default;
      const response = await apiClient.get(`/albums/${albumId}`);
      const songs: AlbumSong[] = response.data.songs || [];
      this.data.albumSongs.set(albumId, songs);
      const album: Album | undefined = response.data.album;
      if (album) {
        const existing = this.data.albums.get(albumId);
        this.data.albums.set(albumId, existing ? { ...existing, ...album } : album);
      }
      this.albumDetailsLoaded.add(albumId);
      // Persist membership so fallback art resolves from cache on the next load.
      try {
        await db.albumSongs.put({ albumId, songs });
      } catch (error) {
        console.error('[AppDataStore] Error persisting album membership:', error);
      }
    })();

    this.albumDetailsPromises.set(albumId, promise);
    try {
      await promise;
    } finally {
      this.albumDetailsPromises.delete(albumId);
    }
  }

  /**
   * Ensure a song's revisions and comments are loaded. Fetches once per session
   * and caches the result; concurrent callers share the same request.
   */
  async ensureSongDetails(songId: number): Promise<void> {
    if (this.detailsLoaded.has(songId)) return;

    const inflight = this.detailsPromises.get(songId);
    if (inflight) return inflight;

    const promise = (async () => {
      const apiClient = (await import('@/services/api/apiClient')).default;
      const response = await apiClient.get(`/songs/${songId}`, {
        params: { includeRevisions: true, includeComments: true },
      });

      // Merge full details over the list summary, preserving the
      // list-only fields the details endpoint does not return.
      const summary = this.data.songs.get(songId);
      this.data.songs.set(songId, {
        ...response.data.song,
        revisionCount: summary?.revisionCount,
        commentCount: summary?.commentCount,
        pinnedRevisionName: summary?.pinnedRevisionName,
        pinnedRevisionId: summary?.pinnedRevisionId,
        hidden: summary?.hidden,
      });

      this.data.revisions.set(songId, response.data.revisions || []);
      this.data.comments.set(songId, response.data.comments || []);
      this.detailsLoaded.add(songId);
    })();

    this.detailsPromises.set(songId, promise);
    try {
      await promise;
    } finally {
      this.detailsPromises.delete(songId);
    }
  }

  getAllSongs(): Song[] {
    return Array.from(this.data.songs.values());
  }

  getAllAlbums(): Album[] {
    return Array.from(this.data.albums.values());
  }

  getAlbum(albumId: number): Album | undefined {
    return this.data.albums.get(albumId);
  }

  getAlbumSongs(albumId: number): AlbumSong[] {
    return this.data.albumSongs.get(albumId) || [];
  }

  getSong(songId: number): Song | undefined {
    return this.data.songs.get(songId);
  }

  getRevisions(songId: number): SongRevision[] {
    return this.data.revisions.get(songId) || [];
  }

  getComments(songId: number): Comment[] {
    return this.data.comments.get(songId) || [];
  }

  addComment(songId: number, comment: Comment): void {
    const comments = this.data.comments.get(songId) || [];
    this.data.comments.set(songId, [...comments, comment]);

    const song = this.data.songs.get(songId);
    if (song) {
      song.commentCount = (song.commentCount || 0) + 1;
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Force a refresh of the song list (and albums) from the network.
   */
  async refresh(): Promise<void> {
    await this.refreshSongList();
    this.refreshAlbums().catch((error) => {
      console.warn('[AppDataStore] Album refresh failed:', error);
    });
  }

  async clear(): Promise<void> {
    this.data.songs.clear();
    this.data.revisions.clear();
    this.data.comments.clear();
    this.data.albums.clear();
    this.data.albumSongs.clear();
    this.data.lastFetchTime = null;
    this.detailsLoaded.clear();
    this.albumDetailsLoaded.clear();
    this.initialized = false;
    try {
      await db.songs.clear();
      await db.albums.clear();
      await db.albumSongs.clear();
    } catch (error) {
      console.error('[AppDataStore] Error clearing cached data:', error);
    }
  }
}

export const appDataStore = new AppDataStore();
